import { ISO_TILE } from './iso';

/**
 * The numbers and colours the placeholder art is drawn to.
 *
 * Kept apart from the drawing itself (`placeholder-art.ts`) because this half
 * imports no framework: the projection, the tests and the browser suite can all
 * read it, whereas anything touching Phaser only runs inside a browser. It is
 * also the shape real art has to arrive in later (TODO row 9) — a tileset of
 * equal-sized frames, a bottom-centre entity origin, one rise for solid tiles.
 */

/** Texture keys. Strings are the only handle Phaser gives us; they live here, once. */
export const CAVE_TILESET = 'cave-tiles';

/**
 * The same picture again, under a second key, cut into frames a sprite asks for
 * by tile index.
 *
 * Rock is drawn one sprite per cell now — a layer per depth band — while the
 * floor is still a tilemap, so the one image has to serve both. It is baked
 * twice rather than framed once because a tilemap measures its tileset by the
 * texture's *default* frame, and adding the first frame to a texture repoints
 * that default at it. Bolt the sprite frames onto the tileset and it measures
 * itself by one frame rather than by the whole image, which at this margin and
 * spacing counts no whole row of tiles at all: Phaser warns `Image tile area
 * not tile size multiple`, the tileset ends up holding no tile at `FLOOR_TILE`,
 * and `putTileAt` throws on the first floor cell. Same art from the same
 * drawing code, so there is still only one definition of it — only the copy
 * carries frames.
 *
 * Collapsing the two keys is caught by `e2e/iso-renderer.spec.ts`, on the first
 * create and again on the second.
 */
export const TILE_SPRITES = 'cave-tile-sprites';

export const ENTITY_TEXTURE = 'placeholder-entity';

/**
 * The other players. Not monsters — those have their own silhouettes now
 * (`monster-art.ts`), because a floor of sixty bodies in the *player* colour
 * would say nothing about what is standing there.
 *
 * A second colour rather than a second shape, and only until real art (row 9)
 * gives entities their own silhouettes. It earns its place now because two
 * capsules that overlap have to be told apart by the pixel underneath them,
 * which is how entity-against-entity depth is proven at all.
 */
export const ENTITY_OTHER_TEXTURE = 'placeholder-entity-other';

/** The stem every monster texture key is built on. One key per archetype and standing. */
export const MONSTER_TEXTURE_PREFIX = 'placeholder-monster-';

/** The two halves of a life bar, baked once and shared by every body on the floor. */
export const MONSTER_BAR_TEXTURE = 'placeholder-monster-bar';
export const MONSTER_BAR_FILL_TEXTURE = 'placeholder-monster-bar-fill';

/**
 * One texture for everything lying on the floor, tinted per drop.
 *
 * A single white lozenge rather than a sprite per kind and tier, because the
 * only thing a placeholder can honestly say about a drop is *what channel it
 * came out of and what tier it rolled* — and `docs/GAME-DESIGN.md` already
 * answers that in colour, with the six rarity hexes it calls "the WoW palette".
 * Baking ten near-identical lozenges to hard-code those hexes into pixels would
 * put the design doc's table in two places; a tint puts it in `ground-art.ts`,
 * where a test can read it.
 *
 * Real art (row 9) replaces the lozenge with a sword, a flask and a pile of
 * coins, and the tint survives it: a rarity colour is a rule about *labels*, not
 * about silhouettes.
 */
export const GROUND_ITEM_TEXTURE = 'placeholder-ground-item';

/**
 * The stem every action mark's texture key is built on. One key per action, so
 * a probe that finds a mark can name which state drew it.
 */
export const ACTION_TEXTURE_PREFIX = 'placeholder-action-';

/**
 * The way down, and the arrow that says which way it is.
 *
 * Two textures rather than one, because they answer two different questions and
 * are drawn in two different places: the stairwell lies on its cell and is the
 * thing a player clicks, and the beacon rides the edge of the view and is the
 * thing a player *finds* (`beacon.ts`).
 */
export const STAIRS_TEXTURE = 'placeholder-stairs';
export const STAIRS_BEACON_TEXTURE = 'placeholder-stairs-beacon';

/**
 * Tile indices into the tileset — and the tileset's first gid, so index and
 * frame line up. They start at `1` because a tilemap reserves `-1` for empty and
 * `0` conventionally reads as empty too.
 *
 * They name the frames of the generated texture as well, so a tile drawn as a
 * sprite and the same tile drawn by the tilemap ask for it by the one name.
 */
export const FLOOR_TILE = 1;
export const ROCK_TILE = 2;

/**
 * How far a rock's top face sits above the floor plane, in pixels.
 *
 * A full tile height rather than a token bump, and that is a correctness
 * requirement rather than taste: a rise of half a tile would place a rock's
 * silhouette exactly at the feet of an entity one cell behind it, so the rock
 * would never cover any of the entity and correct depth sorting would be
 * indistinguishable from broken depth sorting. `art-spec.test.ts` pins it.
 */
export const ROCK_RISE = ISO_TILE.height;

/**
 * Tileset frames are tall enough to hold a raised rock and are drawn with a
 * `tileOffset` of the same amount, so the bottom band of every frame lands
 * exactly on the tile's diamond footprint — Tiled's own convention for tiles
 * taller than the grid.
 */
export const TILE_FRAME = { width: ISO_TILE.width, height: ISO_TILE.height + ROCK_RISE } as const;

/**
 * Where inside its frame a tile is anchored, as Phaser's fractional origin.
 *
 * The middle of the diamond in the frame's bottom band: horizontally centred,
 * and `ROCK_RISE + ISO_TILE.height / 2` down. Place a tile sprite at
 * `cellToScreen(cell)` with this origin and its footprint lands exactly on the
 * cell while its rise hangs above — the same alignment a tilemap gets from the
 * `tileOffset` its tileset is registered with, said once for a single image.
 */
export const TILE_ORIGIN = {
  x: ISO_TILE.width / 2 / TILE_FRAME.width,
  y: (ROCK_RISE + ISO_TILE.height / 2) / TILE_FRAME.height,
} as const;

/**
 * Padding around and between the frames of the tileset image, in pixels.
 *
 * Not optional. A rock's outline is stroked along the edge of its own frame, and
 * a one-pixel line centred on a boundary spills half of itself over that
 * boundary — straight into the neighbouring frame, where it reappears at
 * runtime as a phantom vertical line down every floor tile. A gutter gives the
 * spill somewhere harmless to land. This is why real tilesets ship with
 * margin and spacing, and it is cheaper to have it from the first tile than to
 * rediscover it with real art.
 */
export const TILESET_MARGIN = 1;
export const TILESET_SPACING = 2;

/** Frames, left to right, in the order their tile indices run. */
const TILESET_COLUMNS = 2;

/** The size of the generated tileset image, gutters included. */
export const TILESET_IMAGE = {
  width: TILESET_MARGIN * 2 + TILE_FRAME.width * TILESET_COLUMNS + TILESET_SPACING * (TILESET_COLUMNS - 1),
  height: TILESET_MARGIN * 2 + TILE_FRAME.height,
} as const;

/** Top-left of a frame within the tileset image. */
export const frameOrigin = (column: number): { readonly x: number; readonly y: number } => ({
  x: TILESET_MARGIN + column * (TILE_FRAME.width + TILESET_SPACING),
  y: TILESET_MARGIN,
});

/** The entity capsule. Drawn with a bottom-centre origin, so it stands on a cell rather than floating over it. */
export const ENTITY_SIZE = { width: 26, height: 56 } as const;

/**
 * A monster's body: shorter than a player and its own width, so a crowd reads as
 * a crowd of *monsters* rather than as more players.
 *
 * Still taller than a tile, which is the one property that is load-bearing
 * rather than aesthetic: two bodies a diagonal step apart have to overlap on
 * screen or a correct depth sort between them cannot be told from a broken one.
 * `art-spec.test.ts` pins it.
 */
export const MONSTER_SIZE = { width: 26, height: 44 } as const;

/**
 * The disc under an elite's feet, in pixels — 2:1, like every other flat thing
 * in this projection, so it reads as lying on the floor.
 *
 * Wider than the body on purpose. The ring is only visible where the body is
 * not, so the margin between the two *is* the elite identity: a glance, and a
 * pixel probe, both land on that margin. Narrower than a tile so an elite's ring
 * cannot be mistaken for the cell next door.
 */
export const MONSTER_RING = { width: 42, height: 20 } as const;

/** The marker over the head of a unique boss. Nothing else on the floor carries one. */
export const MONSTER_CROWN = { width: 16, height: 10 } as const;

/** Clear air between the crown and the head, so the two read as separate marks. */
export const MONSTER_CROWN_GAP = 3;

/**
 * One monster frame: wide enough for the ring, tall enough for the crown, the
 * body, and the half of the ring that hangs below the feet.
 *
 * One size for all twelve textures, elite or not, so the origin is one constant
 * and a body does not shift on screen the moment it turns out to be a champion.
 */
export const MONSTER_FRAME = {
  width: MONSTER_RING.width,
  height: MONSTER_CROWN.height + MONSTER_CROWN_GAP + MONSTER_SIZE.height + MONSTER_RING.height / 2,
} as const;

/** How far above its feet a monster's frame reaches — the top of a crown, whether or not it has one. */
export const MONSTER_HEAD_RISE = MONSTER_FRAME.height - MONSTER_RING.height / 2;

/**
 * Where inside its frame a monster is anchored: horizontally centred, and at the
 * middle of the ring rather than at the bottom of the image.
 *
 * That is what puts the feet — and therefore `gridToScreen` of the position the
 * server sent — in the middle of the cell, with the near half of the ring
 * hanging over the cell's front edge exactly as a shadow would.
 */
export const MONSTER_ORIGIN = {
  x: 0.5,
  y: MONSTER_HEAD_RISE / MONSTER_FRAME.height,
} as const;

/**
 * A drop on the floor: a flat lozenge, 2:1 like every other flat thing in this
 * projection, so it reads as *lying on* the cell rather than standing on it.
 *
 * Narrower than a tile by a wide margin, for the reason `MONSTER_RING` is: three
 * drops can share one cell (`drops.ts` rolls three independent channels), and a
 * lozenge as wide as the diamond would leave nothing to aim at and no way to see
 * that there is more than one thing there. It is also the whole of the click
 * target — `targeting.ts` hit-tests exactly this box — so it must be big enough
 * to hit and small enough that clicking the floor beside it still walks.
 */
export const GROUND_ITEM_SIZE = { width: 28, height: 14 } as const;

/**
 * How far a drop is drawn above the middle of its cell, in pixels.
 *
 * Zero is not the answer: a body standing on the same cell is anchored at the
 * cell centre too, so an unraised drop would sit exactly under the feet of
 * whoever is looting it and be unclickable while they stand there. A few pixels
 * back — *up* the screen, which in this projection is *away* from the camera —
 * puts it at the back of the diamond, where the floor is visible past a body.
 */
export const GROUND_ITEM_RISE = 6;

/**
 * The stairwell: a flat well sunk into its cell, 2:1 like every other flat thing
 * in this projection so it reads as a hole in the floor rather than a box on it.
 *
 * **Wider than a drop and narrower than a tile**, and both halves of that are
 * load-bearing. It is the one cell on a 128×128 floor a player is deliberately
 * trying to click, so it has to be a target rather than a detail — a drop's
 * 28×14 lozenge is what you look for once you already know where it is. And
 * `targeting.ts` hit-tests exactly this box, axis-aligned, over a diamond that is
 * `ISO_TILE.width` across: a footprint any nearer a full tile would spill into
 * the four diagonal neighbours and turn "walk one cell past the stairs" into a
 * descent.
 */
export const STAIRS_SIZE = { width: 44, height: 22 } as const;

/** How many steps are drawn down into the well. Three reads as a descent; two reads as a rim. */
export const STAIRS_STEPS = 3;

/**
 * The arrow that points at a way down which is off screen.
 *
 * Square, because it is **rotated** to whatever bearing the stairs lie on
 * (`beacon.ts`) and a rotated rectangle changes how much of the view it covers
 * as it turns. Big enough to find at a glance on a 960×540 canvas and small
 * enough not to be mistaken for something standing on the floor.
 */
export const STAIRS_BEACON_SIZE = { width: 22, height: 22 } as const;

/**
 * How far inside the viewport's edge the beacon is drawn, in world pixels.
 *
 * **Half the marker's diagonal**, taken from the marker rather than typed in:
 * the arrow is rotated about its middle, so the furthest any of its corners
 * reaches from the point it is drawn at is `hypot(width, height) / 2` — at any
 * bearing. Anything less and the arrow is clipped by the edge it is pinned to,
 * for exactly the bearings where it is doing its job.
 */
export const STAIRS_BEACON_INSET = Math.hypot(STAIRS_BEACON_SIZE.width, STAIRS_BEACON_SIZE.height) / 2;

/** The life bar, and the fill inside its border. */
export const MONSTER_BAR = { width: 30, height: 6 } as const;
export const MONSTER_BAR_BORDER = 1;
export const MONSTER_BAR_FILL = {
  width: MONSTER_BAR.width - MONSTER_BAR_BORDER * 2,
  height: MONSTER_BAR.height - MONSTER_BAR_BORDER * 2,
} as const;

/** Air between the crown and the bar. */
export const MONSTER_BAR_GAP = 4;

/**
 * How far above the feet the bar's bottom edge sits.
 *
 * Clear of the crown, so a unique boss shows both marks — and the same height
 * for every monster, so a row of bars reads as a row rather than as a staircase.
 */
export const MONSTER_BAR_RISE = MONSTER_HEAD_RISE + MONSTER_BAR_GAP;

/**
 * **The band an action is drawn in: the shoulders**, as a fraction of whatever
 * body it is drawn over.
 *
 * A body already answers four questions in four places, and every one of them
 * has a reader — a probe, and a player's eye — aimed at it: the ring under the
 * feet is the elite tier, the middle of the body is the archetype
 * (`monsters.spec.ts` fires `PROBE_BODY` at exactly half height), the top of
 * the head is the crown, and above that is the life bar. **The one band nothing
 * had claimed is between the chest and the head**, so that is where an action
 * goes.
 *
 * Three quarters rather than a pixel count, so the mark sits in the same place
 * on a 44-pixel monster and a 56-pixel player: a row of bodies shows a row of
 * marks, which is the same argument `MONSTER_BAR_RISE` makes for holding every
 * bar at one height rather than at each body's own.
 *
 * `action-art.test.ts` pins the consequences — that no mark reaches the chest,
 * the ring's margin or the crown — numerically, on both bodies.
 */
export const ACTION_MARK_RISE = 0.75;

/** Flat on the floor, at the feet: where a walk's dust and a corpse's blood lie. */
export const GROUND_MARK_RISE = 0;

/**
 * The marks, by the shape each one is drawn as. Width is the whole of how loud
 * a mark is — a mark wider than the body it is over breaks the silhouette, and
 * a broken silhouette is what the eye finds in a crowd of sixty — so the widths
 * run in the order of what a player has to do something about.
 *
 * `ACTION_GATHER` is the one entry that is quiet for a different reason, and it
 * says so where it is defined: a wind-up is the state a floor in melee wears
 * most of the time, so its loudness answers to *how many bodies are showing it
 * at once* rather than to how much it matters — the same trade `ACTION_DUST`
 * makes, and the reason the blow it turns into stays the widest mark here.
 *
 * All seven are shorter than half a body, which is what keeps the chest clear on
 * a monster as well as on the taller player capsule.
 */

/** A swing, mid-blow: the widest mark there is, and the one that must never be missed. */
export const ACTION_SLASH = { width: 48, height: 16 } as const;

/** A cast: two motes, one at each end, with clear air between them. */
export const ACTION_MOTES = { width: 44, height: 16 } as const;

/** A blow landing on this body. */
export const ACTION_BURST = { width: 40, height: 16 } as const;

/** A guard held up. Hollow — the only mark the body shows through. */
export const ACTION_GUARD = { width: 36, height: 16 } as const;

/**
 * **A blow being gathered**: the swing's own lens, still folded into the body it
 * is about to come out of.
 *
 * Exactly as wide as a body and never wider, which is the whole difference
 * between this mark and the one it turns into. `ACTION_SLASH` is 48 across a
 * 26-pixel body *on purpose* — a silhouette broken out both sides is what the
 * eye finds among sixty — and a wind-up is the same picture before that
 * happens: it reaches the body's own edges and stops there, so the strike reads
 * as the moment it breaks out.
 *
 * Quiet is a legibility requirement here rather than modesty, and the arithmetic
 * is core's: the neutral profile is a twelve-tick swing whose blow lands on tick
 * seven (`attackWindUpTicks`), so a pack in melee wears this mark on more ticks
 * than it wears the strike. A wind-up as loud as a blow landing would drown the
 * one body that has already committed — the argument `ACTION_DUST` wins against
 * sixty walkers, applied to the state that is on screen most of a fight.
 */
export const ACTION_GATHER = { width: 26, height: 16 } as const;

/**
 * Dust at the feet of a body that is walking. **Deliberately the quietest mark
 * there is**: on the tick a floor of sixty closes on a player every one of them
 * is walking, so a loud one would drown the single body that is swinging.
 *
 * Narrower than a body, which is what keeps it out of the margin where an
 * elite's ring is read.
 */
export const ACTION_DUST = { width: 24, height: 8 } as const;

/**
 * The ground under a corpse. Narrower than a body for `ACTION_DUST`'s reason,
 * and 2:1 like every other flat thing in this projection so it reads as lying
 * on the floor rather than standing on it.
 */
export const ACTION_POOL = { width: 24, height: 12 } as const;

/**
 * How far a corpse is turned, in degrees.
 *
 * A quarter turn about the feet — a body falling over where it stood. It is the
 * one thing an action does to the body itself rather than beside it, and it can
 * be afforded here precisely because a corpse is not a target: `targeting.ts`
 * hit-tests a *monster's* box from its position, and a monster is never on this
 * wire as dead (`packages/schema/src/actions.ts`).
 */
export const CORPSE_ANGLE = 90;

/**
 * The palette. Three faces of rock at three brightnesses is the whole lighting
 * model for now — enough for the eye to read a solid block, and enough for a
 * test to tell rock from entity by colour alone, which is what makes occlusion
 * a question a pixel can answer.
 *
 * The two entity colours are that same argument applied to a crowd: an entity
 * covering another entity is only provable while a pixel can say which of the
 * two drew it.
 */
export const PALETTE = {
  floor: 0x4a4133,
  floorEdge: 0x2b251c,
  rockTop: 0x7b8290,
  rockLeft: 0x474d59,
  rockRight: 0x2f333c,
  rockEdge: 0x1a1c22,
  entity: 0x2fd4c8,
  entityEdge: 0x0a3a37,
  entityOther: 0xe0842f,
  entityOtherEdge: 0x3a2008,
  /**
   * One colour per archetype — melee, ranged, caster, the three
   * `docs/GAME-DESIGN.md` names — because the archetype is the only thing the
   * wire says about what a body *does*, and a player deciding whether to close
   * or to back off has to read it in the half-second before it reaches them.
   * The shapes differ too (`placeholder-art.ts`); the colour is what survives a
   * body being half behind a rock.
   */
  monsterMelee: 0xff3b6b,
  monsterRanged: 0x7ae03b,
  monsterCaster: 0xb14bff,
  monsterEdge: 0x140a1e,
  /**
   * The two elite rings. A champion is blue and a unique pack is gold — the boss
   * and its minions share the gold, because they *are* one pack, and only the
   * boss is crowned.
   */
  eliteChampion: 0x3b6bff,
  eliteUnique: 0xffe14a,
  /**
   * The life bar. The full half is near-white so it reads against every body
   * colour above and every floor colour behind; the empty half is dark, which is
   * what makes "no bar at all" mean dead and nothing else.
   */
  monsterLife: 0xf2f2f2,
  monsterLifeEmpty: 0x30161c,
  monsterLifeEdge: 0x0d0709,
  /**
   * A drop, before it is tinted. **White on purpose**: Phaser's tint multiplies,
   * so a lozenge baked in any other colour could not be tinted *to* the rarity
   * hexes `docs/GAME-DESIGN.md` names, only towards them.
   */
  groundItem: 0xffffff,
  groundItemEdge: 0x141018,
  /**
   * The three kinds that have no tier, and a gear entry that arrived without
   * one.
   *
   * They need colours of their own because the rarity palette has none for them
   * — a potion and a pile of coins are not "normal quality", they are not on
   * that axis at all. The scroll is the one that has to be unmistakable rather
   * than merely distinct: `docs/GAME-DESIGN.md` calls a respec *"a misclick from
   * ending a character"*, so it gets a colour no tier uses.
   */
  dropPotion: 0xff4d4d,
  dropGold: 0xffc94a,
  dropRespec: 0x59d8ff,
  /**
   * What a body is **doing**, in seven colours that no body already wears.
   *
   * The colour axis was spent before this milestone reached it: three
   * archetypes, two elite rings, a life bar and six rarity tiers. So an action
   * is drawn in *shape and place* first — a band of its own at the shoulders,
   * a silhouette per state — and colour is what separates two marks from each
   * other rather than what carries the meaning on its own.
   *
   * Every one of them is nonetheless held further from every colour a body
   * wears than `e2e/support.ts`'s probe tolerance, and `action-art.test.ts`
   * asserts exactly that: a probe aimed at an archetype must never be answered
   * by an action, or the reading a player depends on would go with the one
   * being added.
   *
   * The semantics are the ones an ARPG has trained: hot for a blow being dealt,
   * white for one landing, cyan for magic, steel for a guard, dust for feet, and
   * dried blood for a body that is not getting up.
   */
  actionStrike: 0xff6a00,
  /**
   * A blow being gathered: the strike's own colour, one heat short of it.
   *
   * The red and the blue channels are `actionStrike`'s exactly and only the
   * green moves, so the two are one fire at two temperatures — an ember, then
   * the flame. That is the point of the pair: a player has to read the second as
   * *the release of the first*, and a hue picked for contrast would make the
   * warning and the blow two unrelated events. The separation is carried by the
   * shape and by the width instead, which survive a body being half behind a
   * rock better than a hue does.
   *
   * The one channel that does move is worth 54 of it — nine times `isColour`'s
   * tolerance — so no probe aimed at one can be answered by the other, which is
   * what `action-art.test.ts` asserts of every action colour and what makes the
   * e2e suite's "a winding-up body drew no striking mark" mean anything.
   */
  actionWindUp: 0xffa000,
  actionCast: 0x00e0ff,
  /**
   * A blow landing. **Not pure white**, which the palette had already spent:
   * `groundItem` is white because a tint multiplies and a lozenge baked in
   * anything else could not be tinted *to* the rarity hexes. So this is white
   * with enough blood in it to be a different colour by more than a probe's
   * tolerance — it still reads as a flash, and no pixel can answer for both it
   * and a drop, or for it and the near-white life bar above the head.
   */
  actionHurt: 0xffe0e8,
  actionBlock: 0xc8d8e8,
  actionWalk: 0x9a8b6e,
  actionDead: 0x8c1226,
  /**
   * The way down, and the arrow that finds it — **one colour for both**, because
   * the whole job of the beacon is to be recognised as *the same thing* the
   * moment the player walks far enough for the stairwell itself to come on
   * screen.
   *
   * A hue nothing else on this floor wears, and it has to be: the colour axis
   * was already spent on three archetypes, two elite rings, a life bar, six
   * rarity tiers and six actions, and this is the one mark a player is
   * deliberately *searching* for rather than reacting to. Every one of the three
   * is held further from every other palette entry than `e2e/support.ts`'s probe
   * tolerance, and `art-spec.test.ts` asserts exactly that.
   */
  stairs: 0x00e05a,
  stairsStep: 0x0a6b38,
  stairsEdge: 0x03231a,
} as const;

/** Line weight on every placeholder shape. One pixel, so tile seams read without eating the faces. */
export const EDGE_WIDTH = 1;
